import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const articles = {
  'reconnexion-a-soi': {
    image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113219/1739112803973_ta9cs1.jpg",
    date: "15 Mars 2024",
    category: "BIEN-ÊTRE",
    title: "L'Art de la Reconnexion à Soi",
    content: `
      <p>Dans notre monde moderne en perpétuel mouvement, il est facile de perdre le contact avec notre essence profonde. La reconnexion à soi n'est pas un luxe, mais une nécessité pour maintenir notre équilibre intérieur et notre bien-être global.</p>

      <h2>Le Pouvoir de la Présence</h2>
      <p>La première étape de la reconnexion à soi commence par la pratique de la présence consciente. Lors de nos retraites, nous créons un espace sacré où vous pouvez enfin ralentir, respirer et vous écouter véritablement. Loin des distractions quotidiennes, vous apprenez à renouer avec vos sensations, vos émotions et vos besoins profonds.</p>

      <h2>Les Pratiques Qui Transforment</h2>
      <p>Nous vous guidons à travers diverses pratiques soigneusement sélectionnées :</p>
      <ul>
        <li>Méditation guidée pour développer la conscience de soi</li>
        <li>Exercices de respiration pour l'ancrage et la présence</li>
        <li>Rituels matinaux pour établir une connexion quotidienne</li>
        <li>Journaling introspectif pour explorer vos pensées et émotions</li>
      </ul>

      <h2>L'Importance du Cadre</h2>
      <p>Le cadre joue un rôle essentiel dans ce processus de reconnexion. Notre villa en bord de mer à l'Île Maurice offre un environnement propice à l'introspection. Le bruit des vagues, la chaleur du soleil et la beauté de la nature deviennent vos alliés dans ce voyage intérieur.</p>

      <h2>Un Voyage Collectif</h2>
      <p>Bien que profondément personnel, ce voyage de reconnexion est enrichi par la présence d'autres femmes partageant la même quête. Les partages en groupe, les moments de vulnérabilité partagée et le soutien mutuel créent une dynamique puissante qui catalyse la transformation.</p>

      <h2>Au-delà de la Retraite</h2>
      <p>La reconnexion à soi n'est pas un événement ponctuel, mais un art à cultiver quotidiennement. Nous vous donnons les outils et les pratiques pour maintenir cette connexion précieuse une fois de retour dans votre quotidien. C'est un cadeau que vous vous faites et qui continue de porter ses fruits bien après la retraite.</p>
    `
  },
  'lacher-prise': {
    image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113202/1739112803057_ndnvhp.jpg",
    date: "12 Mars 2024",
    category: "TRANSFORMATION",
    title: "Le Pouvoir du Lâcher-Prise",
    content: `
      <p>Le lâcher-prise est souvent mal compris. Il ne s'agit pas d'abandonner ou de renoncer, mais plutôt de libérer ce qui ne nous sert plus pour faire place à de nouvelles possibilités.</p>

      <h2>Comprendre le Lâcher-Prise</h2>
      <p>Le lâcher-prise est un processus actif qui demande du courage et de la conscience. C'est l'art de relâcher notre emprise sur ce que nous ne pouvons pas contrôler, pour nous concentrer sur ce qui est vraiment en notre pouvoir.</p>

      <h2>Les Obstacles au Lâcher-Prise</h2>
      <p>Plusieurs facteurs peuvent nous empêcher de lâcher prise :</p>
      <ul>
        <li>La peur de l'inconnu</li>
        <li>Les croyances limitantes</li>
        <li>Les schémas de contrôle</li>
        <li>Les attentes irréalistes</li>
      </ul>

      <h2>Techniques de Libération</h2>
      <p>Durant notre retraite, nous explorons diverses techniques pour faciliter le lâcher-prise :</p>
      <ul>
        <li>Pratiques de respiration consciente</li>
        <li>Exercices de libération émotionnelle</li>
        <li>Rituels symboliques</li>
        <li>Méditations guidées</li>
      </ul>

      <h2>Les Bienfaits du Lâcher-Prise</h2>
      <p>Quand nous apprenons à lâcher prise, nous expérimentons :</p>
      <ul>
        <li>Une plus grande paix intérieure</li>
        <li>Une diminution du stress et de l'anxiété</li>
        <li>Des relations plus authentiques</li>
        <li>Une ouverture aux nouvelles opportunités</li>
      </ul>
    `
  },
  'cultiver-confiance': {
    image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113194/1736490672772_2_ffkp1w.jpg",
    date: "10 Mars 2024",
    category: "DÉVELOPPEMENT PERSONNEL",
    title: "Cultiver sa Confiance au Quotidien",
    content: `
      <p>La confiance en soi est comme un muscle qui se renforce avec la pratique. C'est une qualité qui peut être cultivée et nourrie quotidiennement à travers des actions conscientes et des rituels positifs.</p>

      <h2>Les Fondements de la Confiance</h2>
      <p>La véritable confiance en soi repose sur plusieurs piliers :</p>
      <ul>
        <li>L'acceptation de soi</li>
        <li>La connaissance de ses valeurs</li>
        <li>La reconnaissance de ses accomplissements</li>
        <li>La capacité à établir des limites saines</li>
      </ul>

      <h2>Rituels Quotidiens</h2>
      <p>Voici quelques pratiques que nous enseignons pour renforcer la confiance :</p>
      <ul>
        <li>Affirmations positives matinales</li>
        <li>Exercices de posture et d'ancrage</li>
        <li>Journal de gratitude</li>
        <li>Visualisation créative</li>
      </ul>

      <h2>Surmonter les Obstacles</h2>
      <p>Le chemin vers une confiance solide comporte des défis. Nous abordons :</p>
      <ul>
        <li>La gestion du syndrome de l'imposteur</li>
        <li>La transformation des critiques en apprentissages</li>
        <li>L'art de célébrer ses victoires</li>
        <li>La construction d'un dialogue intérieur bienveillant</li>
      </ul>

      <h2>L'Impact sur Votre Vie</h2>
      <p>Une confiance en soi authentique transforme tous les aspects de votre vie :</p>
      <ul>
        <li>Relations plus épanouissantes</li>
        <li>Carrière plus alignée</li>
        <li>Prise de décision facilitée</li>
        <li>Bien-être émotionnel accru</li>
      </ul>
    `
  }
};

const BlogPost = () => {
  const { id } = useParams();
  const article = articles[id as keyof typeof articles];

  if (!article) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-serif mb-4">Article non trouvé</h1>
          <Link to="/blog" className="text-[#c9ab81] hover:underline">
            Retourner au blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24">
      {/* Hero Image */}
      <div className="relative h-[70vh] overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <img 
            src={article.image} 
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </motion.div>
        
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div 
            className="text-center max-w-4xl mx-auto px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="flex items-center justify-center space-x-4 text-[#c9ab81] mb-6">
              <span className="font-serif">{article.date}</span>
              <span className="text-white/60">•</span>
              <span className="uppercase tracking-wider text-sm">{article.category}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-serif text-white mb-8">
              {article.title}
            </h1>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-20">
        <Link 
          to="/blog"
          className="inline-flex items-center space-x-2 text-[#c9ab81] hover:text-[#41392e] transition-colors duration-300 mb-12"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour aux articles</span>
        </Link>

        <motion.div 
          className="prose prose-lg max-w-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </div>
    </div>
  );
};

export default BlogPost;